import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { Groups as GroupsIcon } from "@mui/icons-material";
import api from "../../services/api";
import toast from "react-hot-toast";

const stringToColor = (str) => {
  if (!str) return "#999";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return `#${"00000".substring(0, 6 - c.length)}${c}`;
};

const formatNumber = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("55")) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith("55")) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return `+${digits}`;
};

const sanitizeContract = (value) => String(value || "").replace(/\s+/g, "");

export default function RequireContractModal({ open, contact, onSaved, onRemindLater }) {
  const [contract, setContract] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setContract("");
  }, [open, contact?.id]);

  const handleSave = async () => {
    const cleaned = sanitizeContract(contract);
    if (!cleaned) {
      toast.error("Informe o número do contrato.");
      return;
    }
    if (!contact?.id) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/contacts/${contact.id}`, {
        ...contact,
        contract: cleaned,
        tagIds: Array.isArray(contact.tags) ? contact.tags.map((t) => t.id) : [],
      });
      toast.success("Contrato salvo.");
      onSaved?.(data);
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !saving) {
      e.preventDefault();
      handleSave();
    }
  };

  const isGroup = !!contact?.isGroup;
  const subtitle = isGroup ? "Grupo de WhatsApp" : formatNumber(contact?.number);

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      fullWidth
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "480px",
          },
        },
      }}
      BackdropProps={{
        style: {
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 30, 0.4)",
        },
      }}
    >
      <DialogTitle id="require-contract-dialog">
        Informar contrato do cliente
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Avatar
            src={contact?.profilePicUrl}
            sx={{
              width: 44,
              height: 44,
              bgcolor: stringToColor(contact?.name),
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {isGroup ? <GroupsIcon /> : (contact?.name || "?").slice(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {contact?.name || "Contato sem nome"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          Política da Unitech exige o contrato vinculado ao contato antes de prosseguir
          com o atendimento.
        </Typography>

        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Número do contrato"
          value={contract}
          onChange={(e) => setContract(sanitizeContract(e.target.value))}
          onPaste={(e) => {
            const pasted = (e.clipboardData || window.clipboardData).getData("text");
            e.preventDefault();
            setContract(sanitizeContract(pasted));
          }}
          onKeyDown={handleKeyDown}
          disabled={saving}
          helperText="Costuma estar no nome do grupo ou no contrato do cliente. Pode copiar e colar."
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onRemindLater}
          color="secondary"
          variant="outlined"
          disabled={saving}
        >
          Lembrar depois
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || !sanitizeContract(contract)}
        >
          Salvar
          {saving && (
            <CircularProgress
              size={20}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-10px",
                marginLeft: "-10px",
              }}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
